import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API, BRAND, LOGOS } from "@/brand";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const { clearCart } = useCart();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          clearCart();
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts.current += 1;
      if (attempts.current < 12) {
        timer = setTimeout(poll, 2000);
      } else {
        setStatus("pending");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clearCart]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#0A0A0B] concrete-grain px-6 text-center" data-testid="payment-success-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="flex flex-col items-center">
        <img src={LOGOS.emblem} alt={BRAND.name} className="logo-blend w-36 mb-8" />
        {status === "checking" && (
          <>
            <h1 className="font-display text-4xl sm:text-6xl chrome-text uppercase">Sealing the Vault…</h1>
            <p className="mt-4 text-xs tracking-[0.3em] text-zinc-500 uppercase" data-testid="payment-status-checking">Confirming your payment</p>
          </>
        )}
        {status === "paid" && (
          <>
            <h1 className="font-blackletter text-5xl sm:text-7xl chrome-text" data-testid="payment-status-paid">Crowned.</h1>
            <p className="mt-6 max-w-md text-xs leading-loose text-zinc-400 uppercase tracking-[0.2em]">
              Order confirmed. Your armour is being pulled from the vault — a receipt is on its way to your inbox.
            </p>
          </>
        )}
        {status === "pending" && (
          <>
            <h1 className="font-display text-4xl sm:text-6xl chrome-text uppercase" data-testid="payment-status-pending">Payment Processing</h1>
            <p className="mt-4 max-w-md text-xs leading-loose text-zinc-500 uppercase tracking-[0.2em]">
              Stripe is still confirming. Check your email for the receipt.
            </p>
          </>
        )}
        {status === "error" && (
          <h1 className="font-display text-4xl sm:text-6xl chrome-text uppercase" data-testid="payment-status-error">Session Not Found</h1>
        )}
        <Link
          data-testid="payment-success-return"
          to="/"
          className="mt-12 border border-zinc-400 bg-zinc-100 px-10 py-4 text-xs font-bold tracking-[0.35em] text-zinc-950 uppercase transition-colors duration-300 hover:bg-transparent hover:text-zinc-100"
        >
          Return to {BRAND.words[1]} {BRAND.words[2]}
        </Link>
      </motion.div>
    </div>
  );
}
